import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Table, Form, Select, Modal, message, Tooltip, Tag, Icon, Input, Radio, Row, Col, Switch, DatePicker, Tabs } from 'antd';
import { constructColumnFilterRadio, constructColumnFilterSearch, constructColumn, handleOnAggregationTableChange } from 'utils/table/table';
import {
  useGetPaginatedWebhook,
  getWebhooks,
  getWebhookByHost,
  updateWebhookConfigure,
  repushWebhook,
  retryWebhook,
  testWebhook
} from 'utils/apis/webhook';
import { getConstants } from 'utils/apis/constants';
import { getHosts } from '../../utils/apis/host';
import { ACCESS_TOKEN_ADMINS } from 'utils/constants';
import { StyledCard } from './Webhook.styles';
import './Webhook.css';
import { withAppContext } from 'context/AppContext';

const { Option } = Select;
const { TabPane } = Tabs;

const isAccessTokenAdmin = user => user && user._id && ACCESS_TOKEN_ADMINS.includes(String(user._id));

const EVENT_LIST = [
  { label: 'New Reservation', value: 'newReservation' },
  { label: 'Cancel Reservation', value: 'cancelReservation' },
  { label: 'Update Reservation (All)', value: 'updateReservation' },
  { label: 'Update Reservation (Contact Only)', value: 'contactOnly' },
  { label: 'Delete Reservation', value: 'deleteReservation' }
];

const MESSAGING_EVENT_LIST = [
  { label: 'Conversation Created', value: 'conversation.created' },
  { label: 'Conversation Linked', value: 'conversation.linked' },
  { label: 'Conversation Updated', value: 'conversation.update' },
  { label: 'Message Created', value: 'message.created' },
  { label: 'Message Updated', value: 'message.updated' },
  { label: 'Message Deleted', value: 'message.deleted' },
  { label: 'Message Status', value: 'message.status' }
];

const EVENT_TYPE_LABELS = {
  newReservation: 'New Reservation',
  cancelReservation: 'Cancel Reservation',
  updateReservation: 'Update Reservation (All)',
  contactOnly: 'Update Reservation (Contact Only)',
  deleteReservation: 'Delete Reservation',
  'conversation.created': 'Conversation Created',
  'conversation.linked': 'Conversation Linked',
  'conversation.update': 'Conversation Updated',
  'message.created': 'Message Created',
  'message.updated': 'Message Updated',
  'message.deleted': 'Message Deleted',
  'message.status': 'Message Status'
};

const pickMessagingChannel = webhook => (webhook && webhook.messaging) || {};

const RESERVATION_ERROR_FIELDS = ['url', 'eventType', 'apiVersion', 'authHeaderName', 'authKey'];
const MESSAGING_ERROR_FIELDS = ['messagingUrl', 'messagingEventType'];
const MESSAGING_API_VERSION = '1';

const optionalHttpUrlRule = {
  validator: (rule, value, callback) => {
    if (!value) {
      callback();
      return;
    }
    try {
      const parsed = new URL(value);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        callback('URL is invalid');
        return;
      }
      callback();
    } catch (ex) {
      callback('URL is invalid');
    }
  }
};

const tabForFormErrors = err => {
  const fields = Object.keys(err || {});
  if (fields.some(field => RESERVATION_ERROR_FIELDS.includes(field))) {
    return 'reservation';
  }
  if (fields.some(field => MESSAGING_ERROR_FIELDS.includes(field))) {
    return 'messaging';
  }
  return null;
};

const Webhook = props => {
  const { getFieldDecorator, getFieldValue } = props.form;
  const { user } = props;
  const canAccessRepush = isAccessTokenAdmin(user);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState({ currentPage: 1, limit: 10 });
  const { isLoading: isGetUserLoading, paginatedData: userList, total, refetch } = useGetPaginatedWebhook(query);
  const [hostList, setHostList] = useState([]);
  const [configureData, setConfigureData] = useState({});
  const [apiVersionOptions, setApiVersionOptions] = useState([{ value: '1', label: '1' }, { value: '2', label: '2' }]);
  const [activeModal, setActiveModal] = useState(null); // null | 'configure' | 'eventDetail' | 'repush'
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [bodycopied, setBodyCopied] = useState(false);
  const [repushLoading, setRepushLoading] = useState(false);
  const [repushHostOptions, setRepushHostOptions] = useState([]);
  const [lastRepushBookings, setLastRepushBookings] = useState([]);
  const [repushForm, setRepushForm] = useState({ hostId: undefined, date: moment() });
  const [configureTab, setConfigureTab] = useState('reservation');
  const [hmacCopied, setHmacCopied] = useState(false);
  const [allowedChannels, setAllowedChannels] = useState({ reservations: false, messaging: false });
  const canReservation = !!allowedChannels.reservations;
  const canMessaging = !!allowedChannels.messaging;

  useEffect(() => {
    GetHost();
  }, []);

  useEffect(() => {
    let isMounted = true;
    getConstants('webhookApiVersion')
      .then(res => {
        if (!isMounted) return;
        if (res && res.status === 200 && res.data && typeof res.data === 'object') {
          const options = Object.entries(res.data)
            .map(([, value]) => ({ value: String(value), label: String(value) }))
            .filter(o => o.value);
          if (options.length > 0) setApiVersionOptions(options);
        }
      })
      .catch(() => {});
    return () => {
      isMounted = false;
    };
  }, []);

  const GetWebhookByHost = async hostId => {
    setLoading(true);
    const res = await getWebhookByHost(hostId);
    setLoading(false);
    const body = (res && res.data) || {};
    const allowed = body.allowedChannels || {};
    setAllowedChannels({
      reservations: !!allowed.reservations,
      messaging: !!allowed.messaging
    });
    const list = Array.isArray(body.webhooks) ? body.webhooks : Array.isArray(body) ? body : [];

    if (list.length === 0) {
      props.form.resetFields();

      const emptyValues = {
        host: hostId,
        enabled: false,
        apiVersion: '2',
        messagingEnabled: false
      };
      props.form.setFieldsValue(emptyValues);
      setConfigureData(emptyValues);
      return;
    }

    const webhook = list[0];
    const messaging = pickMessagingChannel(webhook);
    const values = {
      url: webhook.url,
      headerAuthentication: webhook.headerAuthentication,
      host: hostId,
      enabled: webhook.enabled,
      authHeaderName: webhook.authHeaderName,
      authKey: webhook.authKey,
      eventType: webhook.eventType,
      apiVersion: webhook.apiVersion != null ? String(webhook.apiVersion) : '2',
      messagingUrl: messaging.url,
      messagingEnabled: !!messaging.enabled,
      messagingEventType: messaging.eventType,
      hmacSecret: messaging.hmacSecret
    };

    props.form.setFieldsValue(values);
    setConfigureData(values);
  };

  const GetHost = async () => {
    setLoading(true);
    const res = await getHosts();
    setLoading(false);

    if (res.status !== 'error') {
      if (res.data.length > 0) {
        GetWebhookByHost(res.data[0]._id);

        setHostList(
          res.data.map(host => ({
            value: host._id,
            label: host.name
          }))
        );
      }
    }
  };

  const RetryWebhook = async id => {
    setLoading(true);
    const res = await retryWebhook(id);
    setLoading(false);

    if (res.status >= 200 && res.status < 300) {
      message.success('Webhook Retry Successfully');
      refetch();
    } else {
      message.error('Failed to Retry Webhook');
    }
  };

  const TestWebhook = async () => {
    const fields =
      configureTab === 'messaging'
        ? ['host', 'messagingUrl', 'messagingEnabled', 'messagingEventType', 'hmacSecret']
        : ['host', 'url', 'enabled', 'eventType', 'apiVersion', 'headerAuthentication', 'authHeaderName', 'authKey'];

    props.form.validateFields(fields, async (err, values) => {
      if (err) {
        return message.warning('Please fill all required fields for testing webhook');
      }

      if (configureTab === 'messaging' && !values.messagingUrl) {
        return message.warning('Please fill all required fields for testing webhook');
      }
      if (configureTab !== 'messaging' && !values.url) {
        return message.warning('Please fill all required fields for testing webhook');
      }

      setLoading(true);
      let res;
      if (configureTab === 'messaging') {
        if (!values.hmacSecret) {
          setLoading(false);
          return message.warning('Save the messaging webhook first to generate an HMAC secret');
        }
        res = await testWebhook({
          channel: 'messaging',
          host: values.host,
          url: values.messagingUrl,
          hmacSecret: values.hmacSecret,
          headerAuthentication: false
        });
      } else {
        if (values.headerAuthentication === false) {
          delete values.authHeaderName;
          delete values.authKey;
        }
        res = await testWebhook(values);
      }
      setLoading(false);
      if (res.status >= 200 && res.status < 300) {
        message.success('Webhook Test Successfully');
      } else {
        message.error('Failed to Test Webhook');
      }
      refetch();
    });
  };

  const buildRepushHostOptionsFromWebhooks = webhooks => {
    if (!Array.isArray(webhooks) || webhooks.length === 0) {
      return [];
    }
    const hostIdsAlreadyAdded = {};
    return webhooks.reduce((acc, w) => {
      const host = w.host;
      const id = host && typeof host === 'object' ? host._id : host || w.hostId;
      const label = w.hostName || (host && typeof host === 'object' && host.name) || id || '';
      if (id && !hostIdsAlreadyAdded[id]) {
        hostIdsAlreadyAdded[id] = true;
        acc.push({ value: id, label });
      }
      return acc;
    }, []);
  };

  const handleOpenRepushModal = () => {
    setActiveModal('repush');
    setLastRepushBookings([]);
    setRepushLoading(true);
    setRepushHostOptions([]);
    getWebhooks()
      .then(webhooks => {
        const options = buildRepushHostOptionsFromWebhooks(webhooks);
        setRepushHostOptions(options);
        setRepushForm({ hostId: undefined, date: moment() });
      })
      .catch(() => {
        message.error('Failed to load webhook configurations');
        setRepushHostOptions([]);
      })
      .finally(() => setRepushLoading(false));
  };

  const handleRepushSubmit = () => {
    const { hostId, date } = repushForm;
    if (!hostId) {
      message.warning('Please select a host');
      return;
    }
    if (!date) {
      message.warning('Please select a date');
      return;
    }
    const dateStr = date.format('YYYY-MM-DD');
    Modal.confirm({
      title: 'Confirm Repush Webhook',
      content: 'This will re-send webhooks for all bookings from the selected date onwards. Are you sure you want to continue?',
      okText: 'Yes, Repush',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        setRepushLoading(true);
        try {
          const result = await repushWebhook(hostId, dateStr);
          setLastRepushBookings(Array.isArray(result) ? result : []);
          message.success(
            Array.isArray(result) && result.length > 0
              ? `Webhook repush completed. ${result.length} booking(s). You can export the CSV below.`
              : 'Webhook repush completed. You can export the CSV below.'
          );
          refetch();
        } catch (err) {
          message.error('Failed to repush webhook');
        } finally {
          setRepushLoading(false);
        }
      }
    });
  };

  const formatBookingDataForCSV = bookings => {
    return bookings.map(booking => {
      const { code, sourceId, startDate, endDate, createdAt, bookingStatus, platform, source, remarks, bookingType } = booking;
      return {
        'Confirmation Code': code || '',
        'Source ID': sourceId || '',
        'Check In Date': startDate || '',
        'Check Out Date': endDate || '',
        'Created At': createdAt ? moment(createdAt).format('DD-MMM-YYYY HH:mm') : '',
        'Booking Status': bookingStatus || '',
        Platform: platform || source || '',
        'Booking Type': String(bookingType != null ? bookingType : ''),
        Remarks: remarks || '',
        'Webhook Migration Date': moment().format('DD-MMM-YYYY hh:mm A')
      };
    });
  };

  const handleExportRepushCSV = () => {
    if (!lastRepushBookings || lastRepushBookings.length === 0) {
      return;
    }
    const hostOption = repushHostOptions.find(h => h.value === repushForm.hostId);
    const hostname = (hostOption && hostOption.label ? hostOption.label : repushForm.hostId || 'export').replace(/\s+/g, '_');
    const rows = formatBookingDataForCSV(lastRepushBookings);
    const headers = Object.keys(rows[0] || {});
    const escape = v => {
      const s = String(v == null ? '' : v);
      if (s.includes(',') || s.includes('"') || s.includes('\n')) return '"' + s.replace(/"/g, '""') + '"';
      return s;
    };
    const csvContent = [headers.join(','), ...rows.map(r => headers.map(h => escape(r[h])).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Repushed_Webhook_${hostname}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const columns = [
    {
      ...constructColumn('Created At', 'createdAt', {
        hasSorter: true
      }),
      render: (value, record) => {
        return moment(value).format('YYYY-MM-DD HH:mm:ss');
      }
    },
    {
      ...constructColumn('Host', 'hostName'),
      ...constructColumnFilterSearch('hostName', 'Search Host')
    },
    {
      ...constructColumn('Reservation Code', 'code'),
      ...constructColumnFilterSearch('code', 'Search Reservation Code')
    },
    {
      ...constructColumn('Event', 'data'),
      render: (value, record) => (
        <Button
          type="link"
          style={{ padding: 0 }}
          onClick={() => {
            setSelectedEvent(record);
            setActiveModal('eventDetail');
          }}
        >
          <div style={{ textDecoration: 'underline' }}>View Details</div>
        </Button>
      )
    },
    {
      ...constructColumn('Event Type', 'eventType'),
      ...constructColumnFilterRadio('eventType', [
        { value: 'newReservation', label: 'New Reservation', key: 'newReservation' },
        { value: 'updateReservation', label: 'Update Reservation (All)', key: 'updateReservation' },
        { value: 'contactOnly', label: 'Update Reservation (Contact Only)', key: 'contactOnly' },
        { value: 'cancelReservation', label: 'Cancel Reservation', key: 'cancelReservation' },
        { value: 'deleteReservation', label: 'Delete Reservation', key: 'deleteReservation' },
        { value: 'conversation.created', label: 'Conversation Created', key: 'conversation.created' },
        { value: 'conversation.linked', label: 'Conversation Linked', key: 'conversation.linked' },
        { value: 'conversation.update', label: 'Conversation Updated', key: 'conversation.update' },
        { value: 'message.created', label: 'Message Created', key: 'message.created' },
        { value: 'message.updated', label: 'Message Updated', key: 'message.updated' },
        { value: 'message.deleted', label: 'Message Deleted', key: 'message.deleted' },
        { value: 'message.status', label: 'Message Status', key: 'message.status' }
      ]),
      render: (value, record) => EVENT_TYPE_LABELS[value] || value
    },
    {
      ...constructColumn(
        <div>
          Attempt{' '}
          <Tooltip title="Every 30 minutes will try to attempt, max 5 attempt.">
            <Icon type="info-circle" />
          </Tooltip>
        </div>,
        'attempt'
      ),
      align: 'center'
    },
    {
      ...constructColumn('Status', 'resStatus'),
      ...constructColumnFilterRadio('resStatus', [
        {
          value: 'success',
          label: 'Success',
          key: 'success'
        },
        {
          value: 'failed',
          label: 'Failed',
          key: 'failed'
        }
      ]),
      onFilter: () => true,
      render: (value, record) => {
        const isSuccess = value >= 200 && value < 300;
        return <Tooltip title={value}>{isSuccess ? <Tag color="green">Success</Tag> : <Tag color="red">Failed</Tag>}</Tooltip>;
      }
    },

    ...(props.checkIsAdmin()
      ? [
          {
            ...constructColumn('Action'),
            align: 'center',
            render: (value, record) => {
              if (record.resStatus !== 200) {
                return (
                  <Button
                    type="link"
                    onClick={() => {
                      RetryWebhook(record._id);
                    }}
                    disabled={loading}
                  >
                    Retry
                  </Button>
                );
              }
              return '-';
            }
          }
        ]
      : [])
  ];

  const handleOnFormSubmit = e => {
    e.preventDefault();

    props.form.validateFields((err, values) => {
      if (err) {
        const tab = tabForFormErrors(err);
        if (tab) {
          setConfigureTab(tab);
        }
        return;
      }

      Modal.confirm({
        title: 'Confirm Configure Webhook?',
        onOk() {
          setLoading(true);
          const payload = {
            host: values.host,
            url: values.url,
            enabled: values.enabled,
            eventType: values.eventType || [],
            apiVersion: values.apiVersion,
            headerAuthentication: values.headerAuthentication,
            ...(values.headerAuthentication ? { authHeaderName: values.authHeaderName, authKey: values.authKey } : {}),
            messaging: {
              url: values.messagingUrl,
              enabled: values.messagingEnabled,
              eventType: values.messagingEventType || [],
              apiVersion: MESSAGING_API_VERSION,
              hmacSecret: values.hmacSecret
            }
          };

          updateWebhookConfigure(payload)
            .then(response => {
              if (!response || response.status < 200 || response.status >= 300) {
                message.error('Failed to update webhook configuration');
                setLoading(false);
                return;
              }

              GetWebhookByHost(values.host);

              message.success('Webhook configuration updated successfully');
              setActiveModal(null);
              setLoading(false);
            })
            .catch(error => {
              message.error('Failed to update webhook configuration');
              setLoading(false);
            });
        }
      });
    });
  };

  const parsedEventData = useMemo(() => {
    if (!selectedEvent) return null;
    try {
      return typeof selectedEvent.data === 'string' ? JSON.parse(selectedEvent.data) : selectedEvent.data;
    } catch {
      return null;
    }
  }, [selectedEvent]);

  return (
    <StyledCard>
      {activeModal === 'eventDetail' && selectedEvent && (
        <Modal
          title="Event Details"
          visible={activeModal === 'eventDetail'}
          onCancel={() => {
            setActiveModal(null);
            setSelectedEvent(null);
            setBodyCopied(false);
          }}
          footer={[
            <Button
              key="close"
              onClick={() => {
                setActiveModal(null);
                setSelectedEvent(null);
              }}
            >
              Close
            </Button>
          ]}
          width={640}
        >
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', marginBottom: 8 }}>Request</div>
            <Row style={{ marginBottom: 8 }}>
              <Col span={8} style={{ color: '#555', fontWeight: 500 }}>
                URL
              </Col>
              <Col span={16} style={{ wordBreak: 'break-all' }}>
                {selectedEvent.url}
              </Col>
            </Row>
            <Row style={{ marginBottom: 8 }}>
              <Col span={8} style={{ color: '#555', fontWeight: 500 }}>
                Method
              </Col>
              <Col span={16}>{selectedEvent.method || 'POST'}</Col>
            </Row>
            <Row style={{ marginBottom: 8 }}>
              <Col span={8} style={{ color: '#555', fontWeight: 500 }}>
                Webhook Version
              </Col>
              <Col span={16}>
                {(selectedEvent.header && selectedEvent.header['x-api-version']) ||
                  (selectedEvent.headers && selectedEvent.headers['x-api-version']) ||
                  '1'}
              </Col>
            </Row>
            <Row style={{ marginBottom: 8 }}>
              <Col span={8} style={{ color: '#555', fontWeight: 500 }}>
                Timestamp
              </Col>
              <Col span={16}>
                {selectedEvent.createdAt ? (
                  <>
                    <div>{moment.utc(selectedEvent.createdAt).format('MMM DD, YYYY HH:mm:ss')} UTC</div>
                    <div>
                      {moment
                        .utc(selectedEvent.createdAt)
                        .utcOffset(8)
                        .format('MMM DD, YYYY HH:mm:ss')}{' '}
                      GMT+8 (Malaysia/Singapore)
                    </div>
                  </>
                ) : (
                  '-'
                )}
              </Col>
            </Row>
            <Row style={{ marginBottom: 8 }}>
              <Col span={8} style={{ color: '#555', fontWeight: 500 }}>
                Response Status
              </Col>
              <Col span={16}>
                <Tooltip title={selectedEvent.resStatus}>
                  {selectedEvent.resStatus >= 200 && selectedEvent.resStatus < 300 ? (
                    <Tag color="green">Success ({selectedEvent.resStatus})</Tag>
                  ) : (
                    <Tag color="red">Failed ({selectedEvent.resStatus})</Tag>
                  )}
                </Tooltip>
              </Col>
            </Row>
            {selectedEvent.responseBody && (
              <Row style={{ marginBottom: 8 }}>
                <Col span={8} style={{ color: '#555', fontWeight: 500 }}>
                  Response Body
                </Col>
                <Col span={16}>{selectedEvent.responseBody}</Col>
              </Row>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ color: '#555', fontWeight: 500 }}>Request Body</span>
            <Tooltip title={bodycopied ? 'Copied!' : 'Copy to clipboard'}>
              <Button
                size="small"
                icon={bodycopied ? 'check' : 'copy'}
                type={bodycopied ? 'primary' : 'default'}
                onClick={() => {
                  const text = parsedEventData ? JSON.stringify(parsedEventData, null, 2) : selectedEvent.data || '';
                  navigator.clipboard.writeText(text).then(() => {
                    setBodyCopied(true);
                    setTimeout(() => setBodyCopied(false), 2000);
                  });
                }}
              />
            </Tooltip>
          </div>
          <div
            style={{
              background: '#1e1e1e',
              color: '#d4d4d4',
              borderRadius: 6,
              padding: '12px 16px',
              fontSize: 12,
              fontFamily: 'monospace',
              maxHeight: 360,
              overflowY: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all'
            }}
          >
            {parsedEventData ? JSON.stringify(parsedEventData, null, 2) : selectedEvent.data || 'No data'}
          </div>
        </Modal>
      )}

      {activeModal === 'configure' && (
        <Modal
          title="Configure Webhook"
          visible={activeModal === 'configure'}
          onCancel={() => setActiveModal(null)}
          width={780}
          bodyStyle={{ padding: 16 }}
          footer={
            <Row type="flex" justify="space-between" style={{ margin: 0 }}>
              <Col>
                <Button
                  key="test"
                  onClick={() => {
                    TestWebhook();
                  }}
                  disabled={loading || (configureTab === 'messaging' ? !canMessaging : !canReservation)}
                >
                  Test Webhook
                </Button>
              </Col>
              <Col>
                <Button key="back" onClick={() => setActiveModal(null)} disabled={loading}>
                  Cancel
                </Button>
                <Button key="submit" onClick={handleOnFormSubmit} type="primary" loading={loading} style={{ marginLeft: 8 }}>
                  Save
                </Button>
              </Col>
            </Row>
          }
        >
          <Form layout="vertical" hideRequiredMark>
            <Form.Item label="Host">
              {getFieldDecorator('host', {
                rules: [{ required: true, message: 'Host is required' }]
              })(
                <Select
                  showSearch
                  placeholder="Select host"
                  optionFilterProp="children"
                  onChange={e => {
                    GetWebhookByHost(e);
                  }}
                  size="default"
                  filterOption={(input, option) => option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                  disabled={loading}
                >
                  {hostList.map((item, index) => {
                    return (
                      <Option key={index} value={item.value}>
                        {item.label}
                      </Option>
                    );
                  })}
                </Select>
              )}
            </Form.Item>

            <Tabs activeKey={configureTab} onChange={setConfigureTab}>
              <TabPane tab="Reservation" key="reservation" forceRender>
                {!canReservation && (
                  <div style={{ marginBottom: 12, color: '#8c8c8c', fontSize: 13 }}>
                    No access token with Reservations scope is mapped to this host.
                  </div>
                )}
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                      <Form.Item label="Enable Webhook">
                        {getFieldDecorator('enabled', {
                          initialValue: false,
                          valuePropName: 'checked'
                        })(<Switch disabled={loading || !canReservation} unCheckedChildren="Off" checkedChildren="On" />)}
                      </Form.Item>

                      <Form.Item label="URL">
                        {getFieldDecorator('url', {
                          rules: [optionalHttpUrlRule, { required: !!getFieldValue('enabled') && canReservation, message: 'URL is required' }]
                        })(<Input placeholder="https://example.com/webhook" disabled={loading || !canReservation} />)}
                      </Form.Item>
                    </div>
                  </Col>

                  <Col xs={24} md={12}>
                    <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                      <Form.Item label="Header Authentication">
                        {getFieldDecorator('headerAuthentication', {
                          initialValue: false,
                          valuePropName: 'checked'
                        })(<Switch disabled={loading || !canReservation} unCheckedChildren="Off" checkedChildren="On" />)}
                      </Form.Item>

                      <Row gutter={12} style={{ display: getFieldValue('headerAuthentication') === false ? 'none' : 'flex' }}>
                        <Col span={12}>
                          <Form.Item label="Header Name">
                            {getFieldDecorator('authHeaderName', {
                              rules: [
                                { required: canReservation && getFieldValue('headerAuthentication') !== false, message: 'Header Name is required' }
                              ]
                            })(<Input placeholder="Authorization" disabled={loading || !canReservation} />)}
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item label="Header Value">
                            {getFieldDecorator('authKey', {
                              rules: [
                                { required: canReservation && getFieldValue('headerAuthentication') !== false, message: 'Header Value is required' }
                              ]
                            })(<Input placeholder="Bearer sk_test_123" disabled={loading || !canReservation} />)}
                          </Form.Item>
                        </Col>
                      </Row>

                      <Form.Item label="Webhook Version">
                        {getFieldDecorator('apiVersion', {
                          initialValue: '2',
                          rules: [{ required: !!getFieldValue('enabled') && canReservation, message: 'Webhook Version is required' }]
                        })(
                          <Select disabled={loading || !canReservation}>
                            {apiVersionOptions.map(opt => (
                              <Option key={opt.value} value={opt.value}>
                                {opt.label}
                              </Option>
                            ))}
                          </Select>
                        )}
                      </Form.Item>
                    </div>
                  </Col>
                </Row>

                <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 12 }}>
                  <Form.Item label="Event">
                    {getFieldDecorator('eventType', {
                      rules: [{ required: !!getFieldValue('enabled') && canReservation, message: 'Please select event' }]
                    })(
                      <Select
                        mode="multiple"
                        style={{ width: '100%' }}
                        filterOption={(input, option) => option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                        disabled={loading || !canReservation}
                      >
                        {EVENT_LIST.map((item, index) => {
                          return (
                            <Option key={index} value={item.value}>
                              {item.label}
                            </Option>
                          );
                        })}
                      </Select>
                    )}
                  </Form.Item>
                </div>
              </TabPane>

              <TabPane tab="Messaging" key="messaging" forceRender>
                {!canMessaging && (
                  <div style={{ marginBottom: 12, color: '#8c8c8c', fontSize: 13 }}>No access token with Messaging scope is mapped to this host.</div>
                )}
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                      <Form.Item label="Enable Webhook">
                        {getFieldDecorator('messagingEnabled', {
                          initialValue: false,
                          valuePropName: 'checked'
                        })(<Switch disabled={loading || !canMessaging} unCheckedChildren="Off" checkedChildren="On" />)}
                      </Form.Item>

                      <Form.Item label="URL">
                        {getFieldDecorator('messagingUrl', {
                          rules: [optionalHttpUrlRule, { required: !!getFieldValue('messagingEnabled') && canMessaging, message: 'URL is required' }]
                        })(<Input placeholder="https://example.com/messaging-webhook" disabled={loading || !canMessaging} />)}
                      </Form.Item>
                    </div>
                  </Col>

                  <Col xs={24} md={12}>
                    <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                      <Form.Item label="HMAC Secret">
                        <div style={{ display: 'flex' }}>
                          {getFieldDecorator('hmacSecret')(<Input disabled placeholder="Generated on save" style={{ flex: 1 }} />)}
                          <Tooltip title={hmacCopied ? 'Copied!' : 'Copy to clipboard'}>
                            <Button
                              icon={hmacCopied ? 'check' : 'copy'}
                              type={hmacCopied ? 'primary' : 'default'}
                              disabled={loading || !canMessaging || !getFieldValue('hmacSecret')}
                              style={{ marginLeft: 8 }}
                              onClick={() => {
                                const secret = getFieldValue('hmacSecret');
                                if (!secret) {
                                  return message.warning('Save first to generate an HMAC secret');
                                }
                                navigator.clipboard.writeText(secret).then(() => {
                                  setHmacCopied(true);
                                  setTimeout(() => setHmacCopied(false), 2000);
                                });
                              }}
                            />
                          </Tooltip>
                        </div>
                      </Form.Item>
                      <Button
                        size="small"
                        disabled={loading || !canMessaging}
                        onClick={() => {
                          const hostId = getFieldValue('host');
                          if (!hostId) {
                            return message.warning('Please select a host');
                          }
                          setLoading(true);
                          updateWebhookConfigure({
                            channel: 'messaging',
                            host: hostId,
                            url: getFieldValue('messagingUrl'),
                            enabled: getFieldValue('messagingEnabled'),
                            eventType: getFieldValue('messagingEventType') || [],
                            regenerateHmac: true
                          })
                            .then(() => {
                              GetWebhookByHost(hostId);
                              message.success('HMAC secret regenerated');
                            })
                            .catch(() => {
                              message.error('Failed to regenerate HMAC secret');
                              setLoading(false);
                            });
                        }}
                      >
                        Regenerate secret
                      </Button>
                    </div>
                  </Col>
                </Row>

                <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 12, marginTop: 12 }}>
                  <Form.Item label="Event">
                    {getFieldDecorator('messagingEventType', {
                      rules: [{ required: !!getFieldValue('messagingEnabled') && canMessaging, message: 'Please select event' }]
                    })(
                      <Select
                        mode="multiple"
                        style={{ width: '100%' }}
                        filterOption={(input, option) => option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                        disabled={loading || !canMessaging}
                      >
                        {MESSAGING_EVENT_LIST.map((item, index) => {
                          return (
                            <Option key={index} value={item.value}>
                              {item.label}
                            </Option>
                          );
                        })}
                      </Select>
                    )}
                  </Form.Item>
                </div>
              </TabPane>
            </Tabs>
          </Form>
        </Modal>
      )}

      {canAccessRepush && activeModal === 'repush' && (
        <Modal
          title="Repush Webhook"
          visible={canAccessRepush && activeModal === 'repush'}
          onCancel={() => !repushLoading && setActiveModal(null)}
          footer={
            <Row type="flex" justify="end">
              <Col>
                <Button key="cancel" onClick={() => setActiveModal(null)} disabled={repushLoading}>
                  Cancel
                </Button>
                <Button key="repush" type="primary" loading={repushLoading} onClick={handleRepushSubmit} style={{ marginLeft: 8 }}>
                  Repush
                </Button>
              </Col>
            </Row>
          }
          width={520}
        >
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Host</div>
            <Select
              showSearch
              placeholder={repushLoading ? 'Loading...' : 'Select host'}
              optionFilterProp="children"
              style={{ width: '100%' }}
              value={repushForm.hostId}
              onChange={v => setRepushForm(f => ({ ...f, hostId: v }))}
              filterOption={(input, option) => option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
              loading={repushLoading}
              disabled={repushLoading}
            >
              {repushHostOptions.map((item, index) => (
                <Option key={item.value || index} value={item.value}>
                  {item.label}
                </Option>
              ))}
            </Select>
            {!repushLoading && repushHostOptions.length === 0 && (
              <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>No webhook configuration found. Configure a webhook first.</div>
            )}
          </div>
          <div style={{ marginBottom: 0 }}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Date</div>
            <DatePicker
              style={{ width: '100%' }}
              value={repushForm.date}
              onChange={d => setRepushForm(f => ({ ...f, date: d }))}
              format="YYYY-MM-DD"
            />
            <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>Bookings from the selected date onwards will be re-pushed</div>
            <Button
              icon="cloud-download"
              type="primary"
              onClick={handleExportRepushCSV}
              disabled={!lastRepushBookings || lastRepushBookings.length === 0}
              style={{ marginTop: 16 }}
            >
              Export CSV
            </Button>
          </div>
        </Modal>
      )}

      <Row type="flex" justify="space-between" style={{ marginBottom: 16 }}>
        <Col>
          <Button
            id="configure-button"
            type="primary"
            icon="tool"
            onClick={() => {
              setConfigureTab('reservation');
              setActiveModal('configure');
              setTimeout(() => {
                props.form.setFieldsValue(configureData);
              }, 100);
            }}
            disabled={loading || hostList.length === 0}
          >
            Configure Webhook
          </Button>
          {canAccessRepush && (
            <Button type="primary" icon="retweet" onClick={handleOpenRepushModal} disabled={loading} style={{ marginLeft: 8 }}>
              Repush Webhook
            </Button>
          )}
        </Col>

        <Col>
          <Tooltip title="This feature is currently under maintenance">
            <Button type="primary" icon="cloud-download" disabled>
              Export CSV
            </Button>
          </Tooltip>
        </Col>
      </Row>

      <Table
        columns={columns}
        loading={isGetUserLoading}
        onChange={(pagination, filters, sorter) => handleOnAggregationTableChange({ pagination, filters, sorter }, setQuery)}
        scroll={{ x: true }}
        dataSource={userList}
        rowKey={record => record._id}
        showSorterTooltip={false}
        pagination={{ total, current: query.currentPage }}
      />
    </StyledCard>
  );
};

export default withAppContext(Form.create()(Webhook));
